import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader, Tree } from "rsuite";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import { Icon } from "components/Icon";


const findIndexByTrunkId = (id, list) => {
    for (let i = 0; i < list.length; i++) {
        if (list[i].trunk_id === id) {
            return i;
        }
    }
    return -1;
};

const TREE_VALUE_KEY = "__dialer_tree_value";

const buildDialerTreeValue = (item, index) => {
    if (item?.trunk_id !== null && item?.trunk_id !== undefined && item?.trunk_id !== "") {
        return `trunk_${item.trunk_id}`;
    }

    return [
        "empty",
        item?.account_id || item?.acc_id || item?.acc_name || "account",
        item?.sp_key || item?.sp_id || item?.sp_name || "price",
        index
    ].join("_");
};

const findTreeValueByTrunkId = (id, list) => {
    if (id === null || id === undefined || id === "") {
        return null;
    }

    const item = list.find(row => row.trunk_id === id);

    return item ? item[TREE_VALUE_KEY] : null;
};

const getOriginalDialerTrunk = (item) => {
    if (!item) {
        return item;
    }

    const { [TREE_VALUE_KEY]: ignoredTreeValue, ...dialerTrunk } = item;

    return dialerTrunk;
};

const TREE_ROW_HEIGHT = {
    desktop: 56,
    mobile: 88
};

const TREE_HEIGHT = {
    collapsed: 168,
    expanded: 360,
    mobile: 360
};


const DialerTrunkList = ({
    trunkId = null,
    disabledTrunk,
    // accounts = null,
    clearTrunk,
    isMobile,
    dialerTrunkList = [],
    dialerLoading = false,

    defaultTrunkId,
    trunkFromTable,
    setTrunkId,
    onChange,
    onClear,
    show
}) => {

    const treeRef = useRef(null);
    const treeListRef = useRef(null);
    const wrapperRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [dialerTreeValue, setDialerTreeValue] = useState(trunkId);
    const rowHeight = isMobile ? TREE_ROW_HEIGHT.mobile : TREE_ROW_HEIGHT.desktop;
    const treeHeight = isMobile ? TREE_HEIGHT.mobile : isExpanded ? TREE_HEIGHT.expanded : TREE_HEIGHT.collapsed;
    const dialerTreeList = useMemo(() => {
        return dialerTrunkList.map((item, index) => ({
            ...item,
            [TREE_VALUE_KEY]: buildDialerTreeValue(item, index)
        }));
    }, [dialerTrunkList]);

    const toggleExpanded = () => {
        setIsExpanded(prevValue => !prevValue);

        requestAnimationFrame(() => {
            if (treeListRef.current && typeof treeListRef.current.resetAfterIndex === "function") {
                treeListRef.current.resetAfterIndex(0, true);
            }
        });
    };

    const releaseTreeFocus = () => {
        requestAnimationFrame(() => {
            const activeElement = document.activeElement;

            if (
                activeElement &&
                wrapperRef.current &&
                wrapperRef.current.contains(activeElement) &&
                typeof activeElement.blur === "function"
            ) {
                activeElement.blur();
            }
        });
    };

    // this need test
    // useEffect(() => {
    //     const accountsId = accounts && accounts.map(item => item.id);
    //     if (trunkId && accountsId) {
    //         const trunks = dialerTrunkList.filter( item => accountsId.includes(item.account_id) );
    //         if (!trunks.some(item => item.trunk_id === trunkId)) {
    //             if (onClear) onClear();
    //         }
    //     }
    // }, [accounts]);

    // set current trunk on init
    useEffect(() => {
        if (!dialerTreeList || !dialerTreeList.length)
            return;

        let currentTrunkId;
        let currentTreeValue;

        if (trunkId) {
            currentTrunkId = trunkId;
            currentTreeValue = findTreeValueByTrunkId(trunkId, dialerTreeList);
        }

        if (defaultTrunkId) {
            currentTrunkId = defaultTrunkId;
            currentTreeValue = findTreeValueByTrunkId(defaultTrunkId, dialerTreeList);
        }

        if (((!trunkFromTable && !trunkId) || (!disabledTrunk && dialerTreeList[0].trunk_id !== trunkId)) && !dialerTreeValue) {
            onChange(getOriginalDialerTrunk(dialerTreeList[0]));

            currentTrunkId = dialerTreeList[0].trunk_id;
            currentTreeValue = dialerTreeList[0][TREE_VALUE_KEY];
        }

        if (trunkFromTable && defaultTrunkId && !trunkId) {
            const defaultTrunk = dialerTreeList.find(dialer => dialer.trunk_id === defaultTrunkId);

            if (defaultTrunk) {
                onChange(getOriginalDialerTrunk(defaultTrunk));

                currentTrunkId = defaultTrunk.trunk_id;
                currentTreeValue = defaultTrunk[TREE_VALUE_KEY];
            }
        }

        if (currentTreeValue) {
            setDialerTreeValue(currentTreeValue);
        }

        const itemIndex = findIndexByTrunkId(currentTrunkId, dialerTreeList);

        focusItem(itemIndex);
    }, [show, dialerTreeList]);

    useEffect(() => {
        const nextTreeValue = findTreeValueByTrunkId(trunkId, dialerTreeList);

        if (!dialerTreeValue || dialerTreeValue !== nextTreeValue)
            setDialerTreeValue(nextTreeValue);

    }, [trunkId, dialerTreeList]);

    useEffect(() => {
        if (!!clearTrunk) {
            setDialerTreeValue(null);
            // ATX-4099 Was commented because it"s causing allocaiton result model doesn"t have a trunkId
            // setTrunkId(null);
        }
    }, [clearTrunk]);

    const renderTreeNode = (item) => {
        if (!item)
            return "";

        return (
            <StyledTreeNode>
                <div className="tree-node-item">
                    <span className="tree-node-item-line">
                        {item.account_manager_name &&
                            <><b className="tree-node-item-line-title">Manager:</b> {item.account_manager_name}&nbsp;</>
                        }
                    </span>
                    <span className="tree-node-item-line">
                        <b className="tree-node-item-line-title">Account:</b> {item.acc_name}&nbsp;
                    </span>
                </div>
                <div className="tree-node-item">
                    <span className="tree-node-item-line">
                        {item?.trunk_id && <><b className="tree-node-item-line-title">Trunk:</b> {item.tr_name}&nbsp;</>}
                    </span>
                    <span className="tree-node-item-line">
                        {item?.sp_key && <><b className="tree-node-item-line-title">Price:</b> {item.sp_name}&nbsp;</>}
                    </span>
                </div>
            </StyledTreeNode>
        )
    };

    const renderTreeHeader = (item) => {
        if (!item)
            return "";

        return (
            <>
                {item.account_manager_name &&
                    <><b>Manager:</b> {item.account_manager_name}&nbsp;</>
                }
                <b>Account:</b> {item.acc_name}&nbsp;
                <b>Trunk: </b> {item.tr_name}&nbsp;
                <b>Price: </b> {item.sp_name}&nbsp;
            </>
        )
    };


    const renderTreeIcon = () => {
        return null;
    };


    const focusItem = (itemIndex) => {
        if (itemIndex < 0 || !treeRef || !treeRef.current)
            return;

        const { current: ref } = treeRef;

        if (treeListRef.current && typeof treeListRef.current.scrollToItem === "function") {
            treeListRef.current.scrollToItem(itemIndex, "center");
            return;
        }

        if (typeof ref.focusNode === "function") {
            ref.focusNode({ refKey: `0-${itemIndex}` });
            return;
        }

        const treeElement = ref.root || ref;
        const listElement = treeElement.querySelector && (
            treeElement.querySelector(".ReactVirtualized__Grid") ||
            treeElement.querySelector(".rs-tree-view") ||
            treeElement.querySelector(".rs-tree")
        );

        if (listElement) {
            listElement.scrollTop = itemIndex * rowHeight;
        }
    };

    return (
        <>
            <Wrapper ref={wrapperRef}>
                <StyledTreeHeader isEmpty={!trunkId} isLoading={dialerLoading && !trunkId}>
                    {trunkId
                        ? <>
                            <ResultString
                                hasValue={trunkId}
                                disabledTrunk
                                onClick={() => {
                                    if (disabledTrunk || !dialerTrunkList.length)
                                        return;

                                    if (trunkId) {
                                        const itemIndex = findIndexByTrunkId(trunkId, dialerTrunkList);
                                        focusItem(itemIndex);
                                    }
                                }}
                            >
                                {dialerTreeList.length
                                    ? renderTreeHeader(dialerTreeList.find(trunk => trunk.trunk_id === trunkId))
                                    : ""
                                }
                            </ResultString>

                            {!disabledTrunk &&
                                <StyledTreeHeaderClear onClick={() => onClear()}>&#x2715;</StyledTreeHeaderClear>
                            }
                        </>

                        : <ResultString
                            disabledTrunk
                            onClick={() => {
                                if (!disabledTrunk && dialerTreeList.length)
                                    focusItem(0);
                            }}
                        >
                            {dialerLoading ? "Loading trunks..." : "Select trunk below"}
                        </ResultString>
                    }

                    {!disabledTrunk && !dialerLoading &&
                        <ExpandButton active={isExpanded} onClick={toggleExpanded} />
                    }

                </StyledTreeHeader>

                {!disabledTrunk &&
                    <TreeWrapper>
                        {dialerLoading && dialerTreeList.length > 0 && <StyledLoader center backdrop content="Refreshing trunks..." />}
                        {dialerLoading && dialerTreeList.length === 0
                            ? <LoadingState style={{ height: treeHeight }}>
                                <Loader size="md" content="Loading trunks..." vertical />
                            </LoadingState>
                            : <StyledTree
                                key={treeHeight}
                                virtualized
                                scrollShadow={false}
                                // disabledItemValues={( emptyAccounts && emptyAccounts.map(item => item.id) ) || []}
                                ref={treeRef}
                                listRef={treeListRef}
                                height={treeHeight}
                                style={{ "--dialer-tree-row-height": `${rowHeight}px` }}
                                labelKey="tr_name"
                                valueKey={TREE_VALUE_KEY}
                                rowHeight={rowHeight}
                                listProps={{
                                    rowHeight,
                                    itemSize: rowHeight
                                }}
                                data={dialerTreeList}
                                value={dialerTreeValue}
                                renderTreeNode={renderTreeNode}
                                renderTreeIcon={renderTreeIcon}
                                onChange={(id) => {
                                    setDialerTreeValue(id);
                                    releaseTreeFocus();
                                    // setTrunkId(id);

                                    const selectedTrunk = dialerTreeList.find(trunk => trunk[TREE_VALUE_KEY] === id);

                                    return onChange
                                        ? onChange(getOriginalDialerTrunk(selectedTrunk))
                                        : onChange
                                }}
                            />
                        }
                    </TreeWrapper>
                }
            </Wrapper>

        </>
    )
};


const mapState = ({ references }) => ({
    // dialerTrunkList: references.dialerTrunkList,
});

export default connect(mapState, {})(DialerTrunkList);


const TreeWrapper = styled.div`
    background: linear-gradient(180deg, #fbfdff 0%, #ffffff 100%);
    position: relative;
`;

const StyledLoader = styled(Loader)`
    && {
        z-index: 4;
    }
`;

const LoadingState = styled.div`
    align-items: center;
    background:
        radial-gradient(circle at 20% 20%, rgba(52, 152, 255, 0.10), transparent 32%),
        linear-gradient(135deg, #f8fbff 0%, #ffffff 55%, #f7f9fc 100%);
    border-top: 1px solid #eef2f7;
    color: #717273;
    display: flex;
    justify-content: center;
`;

const StyledTree = styled(Tree)`
    && > .rs-tree > .rs-tree-virt-auto-sizer,
    && > .rs-tree > .ReactVirtualized__Grid.ReactVirtualized__List,
    && .rs-tree-virt-list {
        overflow-anchor: none;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
            height: 0;
            width: 0;
        }
    }

    && {
        overflow: hidden !important;

        .rs-tree-node > .rs-tree-node-label > .rs-tree-node-label-content {
            font-size: 13px;
            font-weight: 400;
            line-height: 1.35;
            height: var(--dialer-tree-row-height);
            box-sizing: border-box;
            overflow: hidden;
            padding: 9px 12px 9px 24px;
            width: 100%;
            white-space: normal;
        }
    }

    .rs-tree-node {
        border-bottom: 1px solid #f1f1f1;
        box-sizing: border-box;
        height: var(--dialer-tree-row-height);
        overflow-anchor: none;
        padding: 0 !important;
        margin: 0;
        overflow: hidden;

        &:last-child {
            border-bottom: none;
        }

        & > .rs-tree-node-label {
            align-items: stretch;
            display: flex;
            font-weight: 400;
            height: var(--dialer-tree-row-height);
            padding-left: 0;
            width: 100%;

            &:before {
                display: none;
            }
        }
    }

    .text-muted span {
        opacity: 0.7;
    }
`;

const Wrapper = styled.div`
    border: 1px solid #e5e5ea;
    border-radius: 6px;
    margin-bottom: 30px;
    overflow: visible !important;
`;

const ResultString = styled.p`
    color: #333333;
    cursor: ${props => props.disabledTrunk ? "default" : "pointer"};
    padding: 10px 12px;

    ${props => !props.hasValue && css`
            opacity: 0.6;
        `
    }
`;

const StyledTreeHeader = styled.div`
    align-items: center;
    background: ${props => props.isLoading
        ? "linear-gradient(90deg, #f8fbff 0%, #eef7ff 50%, #f8fbff 100%)"
        : props.isEmpty ? "transparent" : "#f2faff"};
    box-shadow: 0 1px 5px -1px rgba(33, 33, 33, 0.2);
    display: flex;
    padding-right: 10px;
    position: relative;
    z-index: 5;
`;

const StyledTreeHeaderClear = styled.span.attrs(props => {
    return {
        role: "button"
    }
})`
    cursor: pointer;
    display: block;
    transition: color 0.2s ease;

    &:hover {
        color: var(--color-brand1);
    }
`;

const ExpandButton = styled(Icon).attrs(props => {
    return {
        icon: "sequence-up"
    }
})`
    cursor: pointer;
    margin-left: auto;
    margin-right: 0px;
    font-size: 18px;
    padding: 0px;
    transition: color 0.2s ease, transform 0.2s ease;
    ${props =>
        props.active && css`
            color: var(--color-brand1);
            transform: rotate(180deg);
        `
    }
    &:hover {
        color: var(--color-brand1);
    }
`;

const StyledTreeNode = styled.div`
    && {
        display: flex;
        flex-direction: column;
        gap: 4px;
        height: 100%;
        justify-content: center;
        overflow: hidden;
        width: 100%;

        .tree-node-item {
            display: flex;
            flex-wrap: wrap;
        }

        .tree-node-item-line {
            display: inline-flex;
            margin-right: 10px;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .tree-node-item-line-title {
            margin-right: 4px;
        }

        @media (max-width: 767px) {
            padding-bottom: 6px;
            padding-top: 6px;

            .tree-node-item {
                flex-direction: column;
            }

            .tree-node-item-line {
                display: flex;
                flex-direction: row;
                margin-right: 0;
            }

            .tree-node-item-line-title {
                margin-right: 5px;
            }
        }
    }
`;
